import Add16 from '@carbon/icons-react/es/add/16';
import Close16 from '@carbon/icons-react/es/close/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import Editor from '@ckeditor/ckeditor5-core/src/editor';
import { FormInstance } from 'antd';
import ButtonGroup from 'antd/lib/button/button-group';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { Button } from 'components/atoms/button';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { Criteria } from 'components/organisms/tasks/criteria';
import { TemplateObjectType } from 'data/templates/api';
import { useActiveDocumentTemplateId } from 'data/templates/documents/hooks/use-active-document-id';
import { useActiveStatusUpdateTemplateId } from 'data/templates/status-updates/hooks/use-active-status-update';
import { setConditionalVariablesAction } from 'data/templates/variables/actions';
import { useTranslation } from 'hooks/use-translation';
import { useProjectFieldsForVariables } from 'queries/variables';
import { excludedProjectFieldsForFilter } from 'utils/project-fields/constants';
import { floatUid } from 'utils/uid';
import variableStyles from 'vendor/ckeditor/plugins/variables/variable.module.css';
import { ConditionalVariablesForm } from './types';

interface Props {
  onClose?: () => void;
  editor?: Editor;
  variableId?: number;
  templateType?: TemplateObjectType;
  formValues?: ConditionalVariablesForm;
}

const StyledButtonGroup = styled(ButtonGroup)`
  display: flex;
  gap: var(--spacing-05);
  justify-content: flex-end;

  Button {
    min-width: 156px;
  }
`;

const TitleCaption = styled.span`
  font-size: var(--font-size-sm);
`;

const StyledCriteria = styled(Criteria)`
  align-items: center;
  gap: var(--spacing-02);

  > * {
    margin-right: 0;

    .ant-select-arrow {
      right: var(--spacing-03);
    }
  }

  > .ant-form-item:nth-child(2) {
    margin-left: var(--spacing-02);
  }
`;

const ConditionBlockLabel = styled.span`
  text-transform: uppercase;
  color: var(--magenta-60);
  font-weight: var(--font-weight-bold);
  display: block;
  width: 100%;
  text-align: left;
  min-width: 50px;
`;

const TrashButton = styled(Button)`
  position: absolute;
  top: 0;
  right: 0;
  width: auto;
`;

const ElseIfBlockWrapper = styled.div`
  position: relative;
  margin-bottom: var(--layout-03);

  ${StyledCriteria} {
    > *:nth-child(3) {
      .ant-select-arrow {
        right: var(--spacing-06);
      }
    }
  }
`;

const StyledDrawerContentTitle = styled(CustomLayoutDrawerContent.Title)`
  padding-bottom: var(--popover-spacing) !important;
  padding-left: var(--spacing-06) !important;
  padding-right: var(--spacing-06) !important;
`;

const StyledCustomLayoutDrawerContent = styled(CustomLayoutDrawerContent)`
  .ant-drawer-close {
    padding-right: var(--spacing-06) !important;
  }
  ${CustomLayoutDrawerContent.Content} {
    padding-left: var(--spacing-06) !important;
    padding-right: var(--spacing-06) !important;
  }
  ${CustomLayoutDrawerContent.Footer} {
    padding-left: var(--spacing-06) !important;
    padding-right: var(--spacing-06) !important;
  }
`;

const Content = ({
  form,
  initialFormValues,
  disallowNewLineInConditionalVariables,
  templateType,
}: {
  form: FormInstance<ConditionalVariablesForm>;
  initialFormValues?: ConditionalVariablesForm;
  disallowNewLineInConditionalVariables?: boolean;
  templateType?: TemplateObjectType;
}) => {
  const { fields: allFields } = useProjectFieldsForVariables({ templateType });
  const placeholderText = useTranslation(
    'variables.conditional.text.placeholder',
  );

  const fields = useMemo(
    () =>
      allFields.filter(
        (x) => !excludedProjectFieldsForFilter.includes(x.fieldName),
      ),
    [allFields],
  );

  const getEditorProps = () => {
    return {
      className: `${variableStyles.form}`,
      placeholder: placeholderText,
      allowSimpleVariables: true,
      disallowNewLine: disallowNewLineInConditionalVariables,
    };
  };

  return (
    <Form
      form={form}
      initialValues={
        initialFormValues ?? {
          ifBlock: {
            condition: {
              field: fields[0],
            },
          },
          elseIfBlocks: [],
          elseBlock: '',
        }
      }
    >
      <Form.Item
        shouldUpdate={(prev, current) => prev.ifBlock !== current.ifBlock}
      >
        {({ getFieldValue, setFieldsValue }) => {
          const ifBlock = getFieldValue('ifBlock');

          return (
            <>
              <StyledCriteria
                fields={fields}
                filter={ifBlock.condition}
                hideRemoveButton
                text={
                  <ConditionBlockLabel>
                    <FormattedMessage id="variables.conditional.if.label" />
                  </ConditionBlockLabel>
                }
                onChange={(_filter) => {
                  setFieldsValue({
                    ifBlock: {
                      ...ifBlock,
                      condition: _filter,
                    },
                  });
                }}
              />
              <RichTextEditor
                {...getEditorProps()}
                data={ifBlock.value ?? ''}
                onChange={(content) =>
                  setFieldsValue({
                    ifBlock: {
                      ...getFieldValue('ifBlock'),
                      value: content,
                    },
                  })
                }
              />
            </>
          );
        }}
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev, current) =>
          prev.elseIfBlocks !== current.elseIfBlocks
        }
      >
        {({ getFieldValue, setFieldsValue }) => {
          const elseIfBlocks = getFieldValue('elseIfBlocks');

          return (
            <>
              {elseIfBlocks.map((_block, index) => (
                <ElseIfBlockWrapper>
                  <StyledCriteria
                    index={index}
                    fields={fields}
                    filter={_block.condition}
                    hideRemoveButton
                    text={
                      <ConditionBlockLabel>
                        <FormattedMessage id="variables.conditional.else.if.label" />
                      </ConditionBlockLabel>
                    }
                    onChange={(_filter) => {
                      setFieldsValue({
                        elseIfBlocks: elseIfBlocks.map((_oldBlock, _index) => {
                          if (index !== _index) {
                            return _oldBlock;
                          }

                          return {
                            ..._oldBlock,
                            condition: _filter,
                          };
                        }),
                      });
                    }}
                  />
                  <RichTextEditor
                    {...getEditorProps()}
                    data={_block.value ?? ''}
                    onChange={(_content) =>
                      setFieldsValue({
                        elseIfBlocks: getFieldValue('elseIfBlocks').map(
                          (_oldBlock, _index) => {
                            if (index !== _index) {
                              return _oldBlock;
                            }

                            return {
                              ..._oldBlock,
                              value: _content,
                            };
                          },
                        ),
                      })
                    }
                  />
                  <TrashButton
                    type="text"
                    icon={<TrashCan16 />}
                    onClick={() => {
                      setFieldsValue({
                        elseIfBlocks: elseIfBlocks.filter(
                          (_, _index) => index !== _index,
                        ),
                      });
                    }}
                  />
                </ElseIfBlockWrapper>
              ))}
              <Button
                type="link"
                icon={<Add16 />}
                iconPlacement="right"
                style={{ padding: 0, marginBottom: 'var(--layout-03)' }}
                onClick={() => {
                  setFieldsValue({
                    elseIfBlocks: [
                      ...elseIfBlocks,
                      {
                        condition: {
                          field: fields[0],
                        },
                      },
                    ],
                  });
                }}
              >
                <FormattedMessage id="variables.conditional.add.button.label" />
              </Button>
            </>
          );
        }}
      </Form.Item>
      <ConditionBlockLabel
        style={{ display: 'block', marginBottom: 'var(--spacing-03)' }}
      >
        <FormattedMessage id="variables.conditional.else.label" />
      </ConditionBlockLabel>

      <Form.Item
        shouldUpdate={(prev, current) => prev.elseBlock !== current.elseBlock}
      >
        {({ getFieldValue, setFieldsValue }) => {
          const elseBlock = getFieldValue('elseBlock');

          return (
            <>
              <RichTextEditor
                {...getEditorProps()}
                data={elseBlock ?? ''}
                onChange={(content) =>
                  setFieldsValue({
                    elseBlock: content,
                  })
                }
              />
            </>
          );
        }}
      </Form.Item>
    </Form>
  );
};

export const ConditionalVariables = ({
  onClose,
  editor,
  variableId,
  templateType,
  formValues: initialFormValues,
}: Props) => {
  const dispatch = useDispatch();

  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm<ConditionalVariablesForm>();
  const activeDocTemplateId = useActiveDocumentTemplateId();
  const activeSUTemplateId = useActiveStatusUpdateTemplateId();
  const disallowNewLineInConditionalVariables = useMemo(
    () =>
      editor?.config.get(
        'variables.disallowNewLineInConditionalVariables',
      ) as boolean,

    [editor],
  );

  const _onClose = useCallback(() => {
    setVisible(false);
    if (onClose) {
      setTimeout(() => requestAnimationFrame(onClose), 300);
    }
  }, [onClose]);

  const _onCancel = useCallback(() => {
    // Clearing variables marker characters {% if present.
    if (!variableId) {
      editor?.model.change((writer: any) => {
        const { model }: { model: any } = editor;
        const mentionMarker = model.markers.get('variables');

        // Create a range on matched text.
        const end = model.createPositionAt(model.document.selection.focus);
        const start = model.createPositionAt(mentionMarker.getStart());
        const range = model.createRange(start, end);
        const selection = writer.createSelection(range);

        model.deleteContent(selection);
      });
    }
    _onClose();
  }, [_onClose, editor, variableId]);

  const _create = useCallback(
    (uuid) => {
      if (!editor) {
        return;
      }

      editor.model.change((writer: any) => {
        const snippet = writer.createElement('conditionalVariables', {
          conditionalVariables: uuid,
          templateType,
        });

        const { model }: { model: any } = editor;
        const mentionMarker = model.markers.get('variables');

        // Create a range on matched text.
        const end = model.createPositionAt(model.document.selection.focus);
        const start = model.createPositionAt(mentionMarker.getStart());
        const range = model.createRange(start, end);
        const selection = writer.createSelection(range);

        model.deleteContent(selection);

        if (editor.getData() === '') {
          const _range = model.createRangeOn(
            model.document.getRoot().getChild(0),
          );

          model.insertContent(snippet, _range);
        } else {
          model.insertContent(snippet);
        }
        editor.execute('insertParagraph', {
          position: model.createPositionAfter(snippet),
        });
      });
    },
    [editor, templateType],
  );

  const _formSubmit = useCallback(
    (_id, _formValues) => {
      dispatch(
        setConditionalVariablesAction({
          variableId: _id,
          formValues: _formValues,
          templateType,
          templateId:
            templateType === TemplateObjectType.Document
              ? activeDocTemplateId
              : templateType === TemplateObjectType.StatusUpdates
              ? activeSUTemplateId
              : undefined,
        }),
      );
    },
    [activeDocTemplateId, activeSUTemplateId, dispatch, templateType],
  );

  const _onSubmit = useCallback(async () => {
    const formValues = form.getFieldValue([]);
    const uuid = variableId ?? floatUid();

    _formSubmit?.(uuid, formValues);

    if (!variableId) {
      _create(uuid);
    } else {
      editor?.setData(editor.getData());
    }
    editor?.editing.view.focus();
    // for existing variables, backend snippet will be generated and saved at the time of saving the template
    // since CKE gets re-rendered when we try to generate backend snippet during data downcast

    _onClose();
  }, [_create, _formSubmit, _onClose, editor, form, variableId]);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  return (
    <CustomLayoutDrawer
      width={796}
      placement="right"
      closeIcon={<Close16 />}
      visible={visible}
      mask
      destroyOnClose
      onClose={_onClose}
      closable
    >
      <StyledCustomLayoutDrawerContent>
        <StyledDrawerContentTitle>
          <FormattedMessage id="variables.conditional.title" />
          <br />
          <TitleCaption>
            <FormattedMessage id="variables.conditional.title.caption" />
          </TitleCaption>
        </StyledDrawerContentTitle>
        <CustomLayoutDrawerContent.Content>
          <Content
            form={form}
            initialFormValues={initialFormValues}
            disallowNewLineInConditionalVariables={
              disallowNewLineInConditionalVariables
            }
            templateType={templateType}
          />
        </CustomLayoutDrawerContent.Content>
        <CustomLayoutDrawerContent.Footer>
          <StyledButtonGroup>
            <Button onClick={_onCancel}>
              <FormattedMessage id="cancel" />
            </Button>
            <Button type="primary" onClick={_onSubmit}>
              <FormattedMessage id="done" />
            </Button>
          </StyledButtonGroup>
        </CustomLayoutDrawerContent.Footer>
      </StyledCustomLayoutDrawerContent>
    </CustomLayoutDrawer>
  );
};
